import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import GenerateDocumentButton from '../components/GenerateDocumentButton'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import { DatePicker } from '@/components/DatePicker'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type OrderLine = {
  id?: number
  description: string
  quantity: string
  unit_price: string
  tax_rate: string
  expense_account: number | null
  product: number | null
  received_quantity?: string
  sort_order: number
  subtotal?: string
  tax_amount?: string
  line_total?: string
}

type PurchaseOrder = {
  id: number
  number: string
  partner: number
  partner_name?: string
  partner_code?: string
  blanket_contract: number | null
  blanket_contract_number: string | null
  vendor_reference: string
  order_date: string
  expected_delivery_date: string | null
  delivery_address: string
  status: 'DRAFT' | 'ISSUED' | 'PARTIAL_RECEIVED' | 'RECEIVED' | 'CLOSED' | 'CANCELLED'
  currency_code: string
  payment_terms_days: number | null
  notes: string
  subtotal: string
  tax_total: string
  total: string
  lines: OrderLine[]
  issued_at: string | null
  created_at?: string
  updated_at?: string
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }
type Account = { id: number; code: string; name: string; account_type: string; is_active: boolean }
type Product = {
  id: number; sku: string; name: string; sales_price: string;
  unit_of_measure: string; is_active: boolean; product_type: string
}
type Warehouse = { id: number; code: string; name: string; is_active: boolean }

const STATUS_COLOR: Record<PurchaseOrder['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ISSUED: 'bg-blue-100 text-blue-800',
  PARTIAL_RECEIVED: 'bg-amber-100 text-amber-800',
  RECEIVED: 'bg-green-100 text-green-800',
  CLOSED: 'bg-purple-100 text-purple-800',
  CANCELLED: 'bg-red-100 text-red-700',
}

type TabKey = 'details' | 'lines' | 'files'

// ─────────────────── Page ───────────────────
export default function ProcurementPurchaseOrderDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const orderId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const orderQ = useQuery({
    queryKey: ['procurement', 'purchase-order', orderId],
    queryFn: async () => (await api.get<PurchaseOrder>(`/procurement/purchase-orders/${orderId}/`)).data,
    enabled: !!orderId,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })
  const productsQ = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: async () => (await api.get<Product[]>('/inventory/products/')).data,
  })
  const warehousesQ = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: async () => (await api.get<Warehouse[]>('/inventory/warehouses/')).data,
  })
  type ContractStub = { id: number; contract_number: string; title: string; contract_type: string }
  const contractsQ = useQuery({
    queryKey: ['finance', 'vendor-contracts'],
    queryFn: async () => (await api.get<ContractStub[]>('/partners/vendor-contracts/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<PurchaseOrder>) =>
      (await api.patch(`/procurement/purchase-orders/${orderId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['procurement', 'purchase-order', orderId] })
      qc.invalidateQueries({ queryKey: ['procurement', 'purchase-orders'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/procurement/purchase-orders/${orderId}/`),
    onSuccess: () => {
      toast.success('Purchase order deleted')
      qc.invalidateQueries({ queryKey: ['procurement', 'purchase-orders'] })
      navigate('/admin/procurement/purchase-orders')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const issueMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/procurement/purchase-orders/${orderId}/issue/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['procurement', 'purchase-order', orderId] })
      qc.invalidateQueries({ queryKey: ['procurement', 'purchase-orders'] })
      toast.success('Purchase order issued')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const receiveMut = useMutation({
    mutationFn: async (payload: { warehouse_id: number; movement_date: string; receipts: { line_id: number; quantity: string }[] }) =>
      (await api.post(`/procurement/purchase-orders/${orderId}/receive/`, payload)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['procurement', 'purchase-order', orderId] })
      qc.invalidateQueries({ queryKey: ['procurement', 'purchase-orders'] })
      qc.invalidateQueries({ queryKey: ['inventory', 'stock-levels'] })
      const mvCount = data?.movements_created ?? 0
      toast.success(`Receipt recorded${mvCount ? ` · ${mvCount} stock movement${mvCount > 1 ? 's' : ''}` : ''}`)
      setReceiveOpen(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')
  const [receiveOpen, setReceiveOpen] = useState(false)

  if (orderQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (orderQ.isError || !orderQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Purchase order not found.</p>
          <Link to="/admin/procurement/purchase-orders" className="text-blue-600 hover:underline">← Back to purchase orders</Link>
        </div>
      </AdminLayout>
    )
  }

  const order = orderQ.data
  const partners = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'VENDOR' || p.partner_type === 'BOTH' || p.partner_type === 'FREELANCER')
  )
  const expenseAccounts = (accountsQ.data || []).filter(
    (a) => a.is_active && a.account_type === 'EXPENSE'
  )
  // POs cover both physical products and services (services can be procured).
  const products = (productsQ.data || []).filter((p) => p.is_active)

  const locked = order.status !== 'DRAFT'
  const canIssue = order.status === 'DRAFT'
  const canReceive = order.status === 'ISSUED' || order.status === 'PARTIAL_RECEIVED'
  const warehouses = (warehousesQ.data || []).filter((w) => w.is_active)

  const save = (payload: Partial<PurchaseOrder>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/procurement/purchase-orders" className="text-muted-foreground hover:text-foreground hover:underline">← Purchase orders</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{order.number}</div>
            <h2 className="text-2xl font-bold text-foreground">
              {order.partner_name || <span className="italic">No vendor</span>}
            </h2>
            <div className="text-sm text-muted-foreground">
              <span>Ordered {order.order_date}</span>
              {order.expected_delivery_date && <span> · Delivery {order.expected_delivery_date}</span>}
              <span> · </span>
              <span className="font-mono">{order.currency_code} {Number(order.total).toFixed(2)}</span>
              {order.vendor_reference && (
                <span> · Vendor ref <span className="font-mono">{order.vendor_reference}</span></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <GenerateDocumentButton
              sourceType="procurement.PurchaseOrder"
              sourceId={order.id}
              label="Generate document…"
            />
            {locked ? (
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[order.status]}`}>{order.status}</span>
            ) : (
              <InlineStatus
                value={order.status}
                colorMap={STATUS_COLOR}
                options={[
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'ISSUED', label: 'Issued' },
                  { value: 'PARTIAL_RECEIVED', label: 'Partial received' },
                  { value: 'RECEIVED', label: 'Received' },
                  { value: 'CLOSED', label: 'Closed' },
                  { value: 'CANCELLED', label: 'Cancelled' },
                ]}
                onSave={(v) => save({ status: v })}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canIssue && (
                  <DropdownMenuItem
                    onClick={() => confirm(
                      `Issue purchase order ${order.number} (locks lines)`,
                      () => issueMut.mutate(),
                    )}
                    disabled={issueMut.isPending}
                    className="text-blue-700"
                  >
                    Issue PO
                  </DropdownMenuItem>
                )}
                {canReceive && (
                  <DropdownMenuItem
                    onClick={() => setReceiveOpen(true)}
                    className="text-green-700"
                  >
                    Receive goods…
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  disabled={order.status !== 'DRAFT' && order.status !== 'CANCELLED'}
                  onClick={() => (order.status === 'DRAFT' || order.status === 'CANCELLED') && confirm(
                    `purchase order ${order.number}`,
                    () => deleteMut.mutate(),
                  )}
                  className={(order.status === 'DRAFT' || order.status === 'CANCELLED') ? 'text-destructive' : ''}
                >
                  {(order.status === 'DRAFT' || order.status === 'CANCELLED') ? 'Delete purchase order' : 'Delete (issued)'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'lines', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'lines' && `Line items (${order.lines.length})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab order={order} partners={partners} contracts={contractsQ.data || []} locked={locked} onSave={save} />
      )}
      {tab === 'lines' && (
        <LinesTab
          order={order}
          expenseAccounts={expenseAccounts}
          products={products}
          locked={locked}
          onSaveLines={(newLines) => save({ lines: newLines } as Partial<PurchaseOrder>)}
        />
      )}
      {tab === 'files' && <FilesTab parent="procurement.purchaseorder" parentId={orderId} />}

      {receiveOpen && (
        <ReceiveDialog
          order={order}
          warehouses={warehouses}
          submitting={receiveMut.isPending}
          onCancel={() => setReceiveOpen(false)}
          onSubmit={(payload) => receiveMut.mutate(payload)}
        />
      )}
    </AdminLayout>
  )
}

// ─────────────────── Receive dialog ───────────────────

function ReceiveDialog({
  order, warehouses, submitting, onCancel, onSubmit,
}: {
  order: PurchaseOrder
  warehouses: Warehouse[]
  submitting: boolean
  onCancel: () => void
  onSubmit: (payload: {
    warehouse_id: number
    movement_date: string
    receipts: { line_id: number; quantity: string }[]
  }) => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [warehouseId, setWarehouseId] = useState<string | undefined>(
    warehouses.length === 1 ? String(warehouses[0].id) : undefined,
  )
  const [movementDate, setMovementDate] = useState<string>(today)

  // Lines with something still outstanding (ordered > received).
  const openLines = (order.lines || []).filter((ln) => {
    const ordered = Number(ln.quantity || 0)
    const received = Number(ln.received_quantity || 0)
    return ordered > received
  })

  // Default each open line's "receive now" to its remaining quantity, so the
  // common "received everything that was on the truck" case is one click.
  const [qtyByLine, setQtyByLine] = useState<Record<number, string>>(() => {
    const out: Record<number, string> = {}
    for (const ln of openLines) {
      if (!ln.id) continue
      const remaining = Number(ln.quantity) - Number(ln.received_quantity || 0)
      out[ln.id] = remaining > 0 ? String(remaining) : '0'
    }
    return out
  })

  const setQty = (lineId: number, value: string) => {
    setQtyByLine((m) => ({ ...m, [lineId]: value }))
  }

  const submit = () => {
    if (!warehouseId) {
      toast.error('Pick a destination warehouse.')
      return
    }
    const receipts = Object.entries(qtyByLine)
      .map(([line_id, quantity]) => ({ line_id: Number(line_id), quantity }))
      .filter((r) => Number(r.quantity) > 0)
    if (receipts.length === 0) {
      toast.error('Enter a positive quantity for at least one line.')
      return
    }
    onSubmit({
      warehouse_id: Number(warehouseId),
      movement_date: movementDate,
      receipts,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40">
      <div className="bg-card rounded-lg shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-1">Receive goods — {order.number}</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Records the receipt, bumps each line's received quantity, and creates
          an inventory IN movement for lines with a product attached.
        </p>

        <FieldGroup>
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel>Destination warehouse</FieldLabel>
              <SearchableSelect
                value={warehouseId}
                onChange={(v) => setWarehouseId(v)}
                options={warehouses.map((w) => ({
                  value: String(w.id), label: w.name, hint: w.code,
                }))}
                placeholder="Pick warehouse…"
                emptyLabel="No active warehouses"
              />
            </Field>
            <Field>
              <FieldLabel>Receipt date</FieldLabel>
              <DatePicker value={movementDate} onChange={setMovementDate} />
            </Field>
          </div>
        </FieldGroup>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground uppercase border-b">
              <tr>
                <th className="text-left py-2">Line</th>
                <th className="text-right py-2">Ordered</th>
                <th className="text-right py-2">Already received</th>
                <th className="text-right py-2">Receive now</th>
              </tr>
            </thead>
            <tbody>
              {openLines.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-muted-foreground italic">
                    All lines are fully received.
                  </td>
                </tr>
              ) : openLines.map((ln) => {
                const ordered = Number(ln.quantity || 0)
                const received = Number(ln.received_quantity || 0)
                const remaining = ordered - received
                return (
                  <tr key={ln.id} className="border-b">
                    <td className="py-2 pr-2">
                      <div className="font-medium text-foreground">{ln.description}</div>
                      {ln.product == null && (
                        <div className="text-xs text-muted-foreground italic">
                          No product — won't create a stock movement
                        </div>
                      )}
                    </td>
                    <td className="text-right py-2">{ordered}</td>
                    <td className="text-right py-2">{received}</td>
                    <td className="text-right py-2 w-32">
                      <Input
                        type="number" min={0} max={remaining} step="0.01"
                        className="text-right"
                        value={ln.id ? qtyByLine[ln.id] ?? '0' : '0'}
                        onChange={(e) => ln.id && setQty(ln.id, e.target.value)}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>Cancel</Button>
          <Button type="button" onClick={submit} disabled={submitting || openLines.length === 0}>
            {submitting ? 'Recording…' : 'Record receipt'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────── Details tab ───────────────────

type ContractStub = { id: number; contract_number: string; title: string; contract_type: string }

function DetailsTab({
  order, partners, contracts, locked, onSave,
}: {
  order: PurchaseOrder
  partners: Partner[]
  contracts: ContractStub[]
  locked: boolean
  onSave: (payload: Partial<PurchaseOrder>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      {locked && order.status !== 'CANCELLED' && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
          This PO is <strong>{order.status.toLowerCase().replace('_', ' ')}</strong> and read-only. Lines and header are locked.
        </div>
      )}
      {order.status === 'CANCELLED' && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
          This purchase order is <strong>cancelled</strong>. Reactivate by changing status before editing.
        </div>
      )}

      <Section title="Order header">
        <Row label="Number">
          <InlineText
            value={order.number}
            onSave={(v) => onSave({ number: v })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Vendor">
          <InlineSelect
            value={String(order.partner)}
            displayLabel={order.partner_name || <Dash />}
            options={partners.map((p) => ({
              value: String(p.id),
              label: `${p.code} ${p.name}`,
              searchText: `${p.code} ${p.name}`,
            }))}
            onSave={(v) => onSave({ partner: Number(v) })}
            disabled={locked}
          />
        </Row>
        <Row label="Vendor reference">
          <InlineText
            value={order.vendor_reference}
            onSave={(v) => onSave({ vendor_reference: v })}
            disabled={locked}
            placeholder="Vendor's quote / order number"
            className="font-mono"
          />
        </Row>
        <Row label="Blanket order">
          <InlineSelect
            value={order.blanket_contract ? String(order.blanket_contract) : ''}
            displayLabel={
              order.blanket_contract_number ? (
                <Link
                  to={`/admin/partners/vendor-contracts/${order.blanket_contract}`}
                  className="text-blue-600 hover:underline font-mono text-xs"
                  onClick={(e) => e.stopPropagation()}
                >
                  {order.blanket_contract_number}
                </Link>
              ) : <span className="text-muted-foreground italic text-xs">none</span>
            }
            allowClear
            options={(contracts)
              .filter((c) => c.contract_type === 'PO')
              .map((c) => ({
                value: String(c.id),
                label: c.contract_number,
                searchText: `${c.contract_number} ${c.title}`,
              }))}
            onSave={(v) => onSave({ blanket_contract: v ? Number(v) : null } as Partial<typeof order>)}
            helper="Optional. Link to the Blanket Order (framework contract) that authorises this PO."
          />
        </Row>
        <Row label="Order date">
          <InlineDate
            value={order.order_date}
            onSave={(v) => onSave({ order_date: v || order.order_date })}
            disabled={locked}
          />
        </Row>
        <Row label="Expected delivery">
          <InlineDate
            value={order.expected_delivery_date}
            onSave={(v) => onSave({ expected_delivery_date: v })}
            disabled={locked}
          />
        </Row>
        <Row label="Currency">
          <InlineText
            value={order.currency_code}
            onSave={(v) => onSave({ currency_code: v.toUpperCase().slice(0, 3) })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Payment terms (days)">
          <InlineNumber
            value={order.payment_terms_days != null ? String(order.payment_terms_days) : ''}
            step="1"
            onSave={(v) => onSave({ payment_terms_days: v === '' ? null : Number(v) })}
            disabled={locked}
            displayFormat={(v) => v ? <span className="font-mono">{v} days</span> : <Dash />}
          />
        </Row>
        <Row label="Status">
          {locked ? (
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[order.status]}`}>{order.status}</span>
          ) : (
            <InlineStatus
              value={order.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'ISSUED', label: 'Issued' },
                { value: 'PARTIAL_RECEIVED', label: 'Partial received' },
                { value: 'RECEIVED', label: 'Received' },
                { value: 'CLOSED', label: 'Closed' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
              onSave={(v) => onSave({ status: v })}
            />
          )}
        </Row>
        {order.issued_at && (
          <Row label="Issued at">
            <span className="text-sm">{new Date(order.issued_at).toLocaleString()}</span>
          </Row>
        )}
      </Section>

      <Section title="Delivery">
        <Row label="Delivery address">
          <InlineText
            value={order.delivery_address}
            multiline
            onSave={(v) => onSave({ delivery_address: v })}
            disabled={locked}
            placeholder="Where the vendor should ship"
          />
        </Row>
      </Section>

      <Section title="Totals">
        <Row label="Subtotal">
          <span className="font-mono">{Number(order.subtotal).toFixed(2)} {order.currency_code}</span>
        </Row>
        <Row label="Tax">
          <span className="font-mono">{Number(order.tax_total).toFixed(2)} {order.currency_code}</span>
        </Row>
        <Row label="Total">
          <span className="font-mono font-bold text-base">
            {Number(order.total).toFixed(2)} {order.currency_code}
          </span>
        </Row>
      </Section>

      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={order.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            disabled={locked}
            placeholder="Internal notes on this purchase order"
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Line items tab ───────────────────

type LineDraft = {
  description: string
  quantity: string
  unit_price: string
  tax_rate: string
  expense_account: number | null
  product: number | null
}

function emptyLineDraft(expenseAccounts: Account[]): LineDraft {
  return {
    description: '',
    quantity: '1',
    unit_price: '0',
    tax_rate: '0',
    expense_account: expenseAccounts[0]?.id ?? null,
    product: null,
  }
}

function LinesTab({
  order, expenseAccounts, products, locked, onSaveLines,
}: {
  order: PurchaseOrder
  expenseAccounts: Account[]
  products: Product[]
  locked: boolean
  onSaveLines: (lines: OrderLine[]) => Promise<void>
}) {
  const { confirm, dialog } = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<LineDraft>(emptyLineDraft(expenseAccounts))
  const [pending, setPending] = useState(false)

  function openCreate() {
    setDraft(emptyLineDraft(expenseAccounts))
    setCreating(true)
  }

  async function saveLineField(idx: number, patch: Partial<OrderLine>) {
    const next = order.lines.map((l, i) => i === idx ? { ...l, ...patch } : l)
    await onSaveLines(serializeLines(next))
  }

  async function addLine() {
    setPending(true)
    try {
      const next: OrderLine[] = [
        ...order.lines,
        {
          description: draft.description,
          quantity: draft.quantity,
          unit_price: draft.unit_price,
          tax_rate: draft.tax_rate,
          expense_account: draft.expense_account,
          product: draft.product,
          sort_order: order.lines.length,
        },
      ]
      await onSaveLines(serializeLines(next))
      toast.success('Line added')
      setCreating(false)
    } catch (e) {
      toast.error(extractErr(e))
    } finally {
      setPending(false)
    }
  }

  async function removeLine(idx: number) {
    const next = order.lines.filter((_, i) => i !== idx)
    try {
      await onSaveLines(serializeLines(next))
      toast.success('Line removed')
    } catch (e) {
      toast.error(extractErr(e))
    }
  }

  function setDraftProduct(productId: number | null) {
    const p = products.find((x) => x.id === productId)
    if (p) {
      setDraft({
        ...draft,
        product: productId,
        description: draft.description || p.name,
        unit_price: draft.unit_price === '0' ? p.sales_price : draft.unit_price,
      })
    } else {
      setDraft({ ...draft, product: productId })
    }
  }

  let subtotal = 0, taxTotal = 0
  order.lines.forEach((l) => {
    const s = Number(l.quantity) * Number(l.unit_price)
    const t = s * Number(l.tax_rate) / 100
    subtotal += s; taxTotal += t
  })

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          {locked
            ? 'PO locked — line items are read-only.'
            : 'Edit quantities, unit prices, and tax rates inline. Picking a product pre-fills description and unit price.'}
        </p>
        <Button size="sm" onClick={openCreate} disabled={locked}>
          <Plus className="h-3 w-3 mr-1" /> Add line
        </Button>
      </div>

      {order.lines.length === 0 ? (
        <p className="text-muted-foreground text-sm">No line items yet.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Description</Th>
                <Th>Product</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Received</Th>
                <Th className="text-right">Unit price</Th>
                <Th className="text-right">Tax %</Th>
                <Th>Expense account</Th>
                <Th className="text-right">Line total</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {order.lines.map((l, i) => {
                const s = Number(l.quantity) * Number(l.unit_price)
                const t = s * Number(l.tax_rate) / 100
                const acct = expenseAccounts.find((a) => a.id === l.expense_account)
                const prod = l.product ? products.find((p) => p.id === l.product) : null
                return (
                  <tr key={l.id ?? i}>
                    <Td className="font-medium">
                      <InlineText
                        value={l.description}
                        onSave={(v) => saveLineField(i, { description: v })}
                        disabled={locked}
                        placeholder="Item description"
                      />
                    </Td>
                    <Td className="text-xs">
                      <InlineSelect
                        value={l.product ? String(l.product) : ''}
                        displayLabel={prod ? <span className="font-mono">{prod.sku}</span> : <Dash />}
                        allowClear
                        options={products.map((p) => ({
                          value: String(p.id),
                          label: `${p.sku} — ${p.name}`,
                          searchText: `${p.sku} ${p.name}`,
                        }))}
                        onSave={(v) => saveLineField(i, { product: v ? Number(v) : null })}
                        disabled={locked}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.quantity}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { quantity: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                      />
                    </Td>
                    <Td className="text-right font-mono text-xs text-muted-foreground">
                      {Number(l.received_quantity ?? 0).toFixed(2)}
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.unit_price}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { unit_price: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.tax_rate}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { tax_rate: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}%</span>}
                      />
                    </Td>
                    <Td className="text-xs">
                      <InlineSelect
                        value={l.expense_account ? String(l.expense_account) : ''}
                        displayLabel={acct ? `${acct.code} ${acct.name}` : <Dash />}
                        allowClear
                        options={expenseAccounts.map((a) => ({
                          value: String(a.id),
                          label: `${a.code} ${a.name}`,
                          searchText: `${a.code} ${a.name}`,
                        }))}
                        onSave={(v) => saveLineField(i, { expense_account: v ? Number(v) : null })}
                        disabled={locked}
                      />
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      {(s + t).toFixed(2)}
                    </Td>
                    <Td className="text-right">
                      {!locked && (
                        <RowActions
                          onEdit={() => {/* inline edits cover the whole row */}}
                          onDelete={() => confirm(
                            `line "${l.description || '(blank)'}"`,
                            () => removeLine(i),
                          )}
                        />
                      )}
                    </Td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot className="bg-muted/40 text-sm">
              <tr>
                <td colSpan={7} className="px-4 py-2 text-right font-medium text-muted-foreground">Subtotal</td>
                <td className="px-4 py-2 text-right font-mono">{subtotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr>
                <td colSpan={7} className="px-4 py-2 text-right font-medium text-muted-foreground">Tax</td>
                <td className="px-4 py-2 text-right font-mono">{taxTotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr className="font-bold">
                <td colSpan={7} className="px-4 py-2 text-right text-foreground">Total</td>
                <td className="px-4 py-2 text-right font-mono">{order.currency_code} {(subtotal + taxTotal).toFixed(2)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="Add line item"
        description="Add a product or service line. Picking a product pre-fills description and unit price."
        onSubmit={addLine}
        isPending={pending}
        submitLabel="Add line"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="li-prod">Product</FieldLabel>
            <SearchableSelect
              id="li-prod"
              value={draft.product ? String(draft.product) : NONE}
              onChange={(v) => setDraftProduct(v === NONE ? null : Number(v))}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...products.map((p) => ({
                  value: String(p.id), label: p.name, hint: p.sku,
                })),
              ]}
            />
            <FieldDescription>Pick a product to pre-fill description and price, or leave blank for a free-text line.</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="li-desc">Description *</FieldLabel>
            <Textarea id="li-desc" autoFocus required value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              rows={2} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="li-qty">Quantity</FieldLabel>
              <Input id="li-qty" type="number" step="0.01" value={draft.quantity}
                onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="li-price">Unit price</FieldLabel>
              <Input id="li-price" type="number" step="0.01" value={draft.unit_price}
                onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="li-tax">Tax %</FieldLabel>
              <Input id="li-tax" type="number" step="0.01" value={draft.tax_rate}
                onChange={(e) => setDraft({ ...draft, tax_rate: e.target.value })}
                className="font-mono text-right" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="li-acct">Expense account</FieldLabel>
            <SearchableSelect
              id="li-acct"
              value={draft.expense_account ? String(draft.expense_account) : NONE}
              onChange={(v) => setDraft({ ...draft, expense_account: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...expenseAccounts.map((a) => ({
                  value: String(a.id), label: a.name, hint: a.code,
                })),
              ]}
            />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

function serializeLines(lines: OrderLine[]): OrderLine[] {
  return lines.map((l, i) => ({
    description: l.description,
    quantity: l.quantity,
    unit_price: l.unit_price,
    tax_rate: l.tax_rate,
    expense_account: l.expense_account,
    product: l.product,
    sort_order: i,
  }))
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
